import React, {
    useEffect, useRef, useState, useMemo, ReactNode,
} from 'react';

import classNames from 'classnames';
import {
    dotSize, activeDotSize, gapSize, dotSizeV2,
    gapSizeV2,
    activeDotSizeV2,
} from './sliderDots.const';
import css from './sliderDots.module.scss';


type SliderDotsWrapperProps = {
    slidesCount: number,
    currentSlideIndex: number,
    children: ReactNode,
    additionalClass?: string
};


export function SliderDotsWrapper({
    slidesCount,
    currentSlideIndex,
    children,
    additionalClass = '',
}: SliderDotsWrapperProps) {
    const isNewVersion = additionalClass === 'v2';
    const wrapRef = useRef<HTMLDivElement>(null);
    const listRef = useRef<HTMLDivElement>(null);

    const [isMounted, setIsMounted] = useState(false);
    useEffect(() => { setIsMounted(true); }, []);

    const listWidth = useMemo(
        () => {
            const currentDotSize = isNewVersion ? dotSizeV2 : dotSize;
            const currentGapSize = isNewVersion ? gapSizeV2 : gapSize;
            const currentActiveDotSize = isNewVersion ? activeDotSizeV2 : activeDotSize;
            return ((slidesCount - 1) * (currentDotSize + currentGapSize)) + currentActiveDotSize;
        },
        [isNewVersion, slidesCount],
    );

    const translate = useMemo(() => {
        if (!isMounted) return 0;
        if (slidesCount <= 3) return 0;

        const lastIndex = slidesCount - 1;
        const isLast = currentSlideIndex === lastIndex;
        const isMiddle = currentSlideIndex > 0 && currentSlideIndex < lastIndex;

        const wrapWidth = wrapRef.current?.clientWidth ?? 0;

        if (isLast) {
            return wrapWidth - listWidth;
        }
        if (isMiddle) {
            return -1 * (currentSlideIndex - 1) * (dotSize + gapSize);
        }

        return 0;
    }, [currentSlideIndex, slidesCount, isMounted, listWidth]);

    const rootClasses = classNames({
        [css.sliderDotsWrap]: true,
        [css[additionalClass]]: Boolean(additionalClass),
    });

    const listClasses = classNames({
        [css.sliderDotsList]: true,
        [css[additionalClass]]: Boolean(additionalClass),
    });


    return (
        <div className={rootClasses} ref={wrapRef}>
            <div
                ref={listRef}
                className={listClasses}
                style={{ transform: `translateX(${translate}px)`, width: listWidth }}
            >
                {children}
            </div>
        </div>
    );
}
